// [SCOPE 051 demo / 053] Authored pt-BR bundle.
//
// Demonstrates the localization framework end-to-end: the keys translated here
// render in Brazilian Portuguese when the locale is pt-BR; any key NOT present
// falls back to en-US per key (FR-002). Currently scoped to the landing page
// (the wired slice) plus common/settings; the full app-wide extraction is spec 052.
//
// Methodology/lifecycle terms and brand/compliance names (wxKanban, SOC 2, HIPAA,
// GDPR, HITRUST) are intentionally left untranslated.
import type { PartialLocaleBundle } from "./en-US";

export const ptBR: PartialLocaleBundle = {
  common: {
    save: "Salvar",
    cancel: "Cancelar",
    close: "Fechar",
    delete: "Excluir",
    edit: "Editar",
    loading: "Carregando…",
    saving: "Salvando…",
    saved: "Salvo",
    error: "Algo deu errado",
    retry: "Tentar novamente",
    yes: "Sim",
    no: "Não",
  },
  settings: {
    language: {
      title: "Idioma",
      description: "Escolha o idioma da interface do app.",
      help: "Os idiomas não traduzidos voltam para o inglês até que seus textos sejam escritos.",
      saved: "Idioma atualizado.",
    },
  },
  landing: {
    nav: {
      home: "Início",
      features: "Recursos",
      pricing: "Preços",
      docs: "Ajuda/Tutoriais",
      signIn: "Entrar",
    },
    hero: {
      subtitle: "Sistema de gestão de projetos com IA",
      description:
        "O wxKanban é um sistema completo de gestão de projetos desenvolvido em torno da IA, com um kit inicial pronto para produção para equipes de qualquer tamanho. Criado para segurança, conformidade e integrações perfeitas.",
      getStarted: "Começar",
      learnMore: "Saiba mais",
    },
    install: {
      eyebrow: "Comece em ~30 minutos",
      heading: "Instalação e configuração",
      description:
        "Novo no wxKanban? Nosso guia passo a passo leva você de um computador zerado a uma configuração funcional no VS Code — instalando o Claude Code, adicionando o Dev Cockpit e preparando seu primeiro projeto. Nenhuma experiência prévia necessária.",
      step1: {
        title: "Instalar as ferramentas",
        body: "VS Code, Node.js e Claude Code — com comandos para copiar e colar.",
      },
      step2: {
        title: "Adicionar o Dev Cockpit",
        body: "Instale a extensão e conecte-a ao seu projeto wxKanban.",
      },
      step3: {
        title: "Executar seu primeiro fluxo de trabalho",
        body: "Scope, spec e build — guiado pelo ciclo de vida de 6 fases.",
      },
      cta: "Ver as instruções de configuração",
    },
    features: {
      heading: "Recursos poderosos",
      subheading:
        "Tudo o que você precisa para gerenciar projetos, controlar o tempo e colaborar com sua equipe",
      aiPowered: {
        title: "Impulsionado por IA",
        description:
          "Sugestões inteligentes de tarefas e otimização de fluxos de trabalho, impulsionadas pela sua ferramenta de IA preferida para desenvolvimento",
      },
      security: {
        title: "Segurança corporativa",
        description: "Em conformidade com SOC 2, HIPAA e LGPD, com criptografia AES-256-GCM",
      },
      integration: {
        title: "Integração multissistema",
        description:
          "Conecte Jira, Monday.com, Asana, Trello e outras plataformas de gestão de projetos com facilidade",
      },
      conversion: {
        title: "Conversão de aplicações legadas",
        intro: "Converta suas aplicações legadas como:",
        more: "e muito mais",
      },
      collaboration: {
        title: "Colaboração em equipe",
        item1:
          "Gestão de equipe em tempo real com controle de acesso baseado em funções",
        item2:
          "Adicione os documentos de SDLC da sua empresa para análise de conformidade",
        item3:
          "Atribua o SDLC a cada membro da equipe para cumprir os controles de auditoria",
        item4:
          "Deixe a IA ajudar você a cumprir todos os requisitos de auditoria",
      },
      lifecycle: {
        title: "Ciclo de vida de 6 fases",
        item1: "Metodologia de qualidade estruturada, de Design a Release",
        item2:
          "Comandos de código aberto e o Claude para gerenciar todo o processo de desenvolvimento",
        item3:
          "Atualize automaticamente as specs e as tarefas no seu sistema de gestão de projetos",
        item4: "Fluxos automáticos para garantir a conformidade com auditorias",
        item5: "Relatório de auditoria completo a cada versão",
        footer:
          "O que você escreve e gera no VS Code, agora em um sistema completo de gestão de projetos",
      },
    },
    pricing: {
      heading: "Preços simples e transparentes",
      subheading:
        "Escolha o plano perfeito para sua equipe. Sempre com a flexibilidade de subir ou descer de nível",
      mostPopular: "MAIS POPULAR",
      perMonth: "/mês",
      free: "Grátis",
      faqText: "Tem dúvidas sobre os preços?",
      contactSales: "Fale com nossa equipe de vendas",
      tiers: {
        single: {
          name: "Usuário único",
          userLimit: "1 usuário",
          f1: "Projetos ilimitados",
          f2: "Uma integração de gestão de projetos",
          f3: "Suporte por e-mail",
          cta: "Começar agora",
        },
        smb: {
          name: "Pequena/Média empresa",
          userLimit: "Até 5 usuários",
          f1: "Certificado SOC 2 e HIPAA",
          f2: "Uma única empresa",
          f3: "Gestão de equipe",
          f4: "Suporte por e-mail",
          cta: "Começar agora",
        },
        consultant: {
          name: "Consultor",
          userLimit: "Equipe ilimitada",
          f1: "Membros de equipe ilimitados",
          f2: "Várias conexões de gestão de projetos",
          f3: "Controle de tempo avançado",
          f4: "Geração de faturas",
          f5: "Fluxos de trabalho personalizados",
          f6: "Acesso à API",
          cta: "Cadastrar",
        },
        enterprise: {
          name: "Enterprise",
          priceUnit: "+ 5 $/usuário acima de 25",
          userLimit: "25+ usuários",
          f1: "25 usuários incluídos",
          f2: "5 $/mês por usuário ativo acima de 25",
          f3: "Tudo ilimitado",
          f4: "Suporte dedicado",
          f5: "Integrações personalizadas",
          f6: "Garantia de SLA",
          cta: "Começar agora",
        },
      },
    },
    compliance: {
      heading: "Segurança de nível corporativo",
      subheading:
        "Criado com a conformidade e a segurança como requisitos fundamentais",
      soc2: {
        name: "Alinhado ao SOC 2",
        description: "Criado segundo os Trust Services Criteria do SOC 2",
      },
      hipaa: {
        name: "Controles orientados à HIPAA",
        description:
          "Salvaguardas baseadas nos padrões de informações de saúde protegidas",
      },
      gdpr: {
        name: "Pronto para a LGPD/RGPD",
        description: "Controles de privacidade e proteção de dados integrados",
      },
      encryption: {
        title: "Criptografia em repouso e em trânsito",
        description: "Criptografia AES-256-GCM para todos os dados",
      },
      auth: {
        title: "Autenticação segura",
        description:
          "Hash bcrypt com tokens JWT e rotação de tokens de atualização",
      },
      audit: {
        title: "Registro de auditoria",
        description: "Trilha de auditoria completa para conformidade e transparência",
      },
      rbac: {
        title: "Controle de acesso baseado em funções",
        description: "Permissões detalhadas para equipes e dados",
      },
      trustLead: "Criado para a confiança",
      trustBody:
        "A segurança dos seus dados é a nossa prioridade máxima. Seguimos padrões de segurança reconhecidos e revisamos nossos controles regularmente.",
    },
    cta: {
      headline: "Pronto para transformar a sua gestão de projetos?",
      subhead: "Junte-se a milhares de equipes que usam o wxKanban para otimizar seus fluxos de trabalho",
      getStarted: "Começar",
      viewPricing: "Ver preços",
    },
    footer: {
      tagline: "Gestão de projetos com tecnologia de IA para equipes modernas",
      product: "Produto",
      company: "Empresa",
      legal: "Jurídico",
      features: "Recursos",
      pricing: "Preços",
      documentation: "Documentação",
      about: "Sobre",
      blog: "Blog",
      contact: "Contato",
      bio: "Biografia",
      privacy: "Privacidade",
      terms: "Termos",
      security: "Segurança",
      rights: "© 2026 wxKanban. Todos os direitos reservados.",
    },
  },
  login: {
    title: "Bem-vindo de volta",
    subtitle: "Entre na sua conta do wxKanban",
    email: "Email",
    emailPlaceholder: "voce@empresa.com",
    password: "Senha",
    rememberMe: "Lembrar de mim",
    forgotPassword: "Esqueceu a senha?",
    signIn: "Entrar",
    signingIn: "Entrando…",
    genericError: "Ocorreu um erro inesperado. Tente novamente.",
    noAccount: "Não tem uma conta?",
    register: "Cadastre sua empresa",
    tagline: "Gestão de tarefas multiplataforma",
    emailLabel: "Endereço de email",
    showPasswordAria: "Segure para mostrar a senha",
    registerLink: "Cadastre-se aqui",
    connectPmPrompt: "Ou conecte um sistema de gestão de projetos para começar:",
    loginFailed: "Falha no login",
  },
  forgotPassword: {
    title: "Esqueceu a senha",
    description: "Digite seu e-mail e enviaremos um link para redefinir sua senha.",
    successMessage: "Se existir uma conta com esse e-mail, um link para redefinir a senha foi enviado. Verifique sua caixa de entrada.",
    backToSignIn: "Voltar ao login",
    emailLabel: "Endereço de e-mail",
    emailPlaceholder: "voce@exemplo.com",
    submit: "Enviar link de redefinição",
    submitting: "Enviando...",
    errorFailed: "Falha ao enviar o link de redefinição",
  },
  resetPassword: {
    title: "Redefinir senha",
    subtitle: "Digite sua nova senha abaixo.",
    newPassword: "Nova senha",
    confirmNewPassword: "Confirmar nova senha",
    updatePassword: "Atualizar senha",
    updating: "Atualizando...",
    successMessage: "Sua senha foi redefinida com sucesso. Redirecionando para o login...",
    backToSignIn: "Voltar ao login",
    errMissingLink: "Este link de redefinição de senha é inválido ou está incompleto.",
    errMissingToken: "O token de redefinição está ausente. Use o link do seu e-mail.",
    errPasswordMin: "A senha deve ter pelo menos 8 caracteres",
    errPasswordMismatch: "As senhas não coincidem",
    errFailed: "Falha ao redefinir a senha",
  },
  changePassword: {
    title: "Altere sua senha",
    welcome: "Bem-vindo, {{name}}! Por segurança, altere sua senha temporária antes de continuar.",
    currentLabel: "Senha atual (temporária)",
    currentPlaceholder: "Digite sua senha temporária",
    newLabel: "Nova senha",
    newPlaceholder: "Crie uma nova senha (mín. 8 caracteres)",
    confirmLabel: "Confirmar nova senha",
    confirmPlaceholder: "Confirme sua nova senha",
    submit: "Alterar senha e continuar",
    needHelp: "Precisa de ajuda? Entre em contato com seu administrador ou com a",
    supportTeam: "equipe de suporte",
    errMinLength: "A nova senha deve ter pelo menos 8 caracteres",
    errMismatch: "As senhas não coincidem",
    errSameAsOld: "A nova senha deve ser diferente da senha atual",
    errFailed: "Falha ao alterar a senha",
    successRedirect: "Senha alterada com sucesso! Redirecionando para o painel...",
  },
  appnav: {
    dashboard: "Painel",
    teamMembers: "Membros da equipe",
    projects: "Projetos",
    pmIntegrations: "Integrações PM",
    myTasks: "Minhas tarefas",
    workHub: "Central de trabalho",
    timeBilling: "Tempo e faturamento",
    invoices: "Faturas",
    reports: "Relatórios",
    settings: "Configurações",
    gettingStarted: "Primeiros passos",
    manual: "Manual", // [SCOPE 141 / T016]
    help: "Ajuda",
    signOut: "Sair",
  },
  register: {
    next: "Próximo",
    loading: "Carregando...",
    back: "Voltar",
    companyTitle: "Informações da empresa",
    companySubtitle: "Conte-nos sobre sua empresa para que possamos configurar seu faturamento e comunicação",
    companyNameLabel: "Nome da empresa *",
    billingEmailLabel: "E-mail de faturamento *",
    billingAddressLabel: "Endereço de faturamento *",
    countryLabel: "País *",
    phoneLabel: "Número de telefone *",
    referredByLabel: "Indicado por (opcional)",
    referredByHint: "Se um distribuidor indicou você, insira o código dele.",
    errCompanyNameRequired: "O nome da empresa é obrigatório",
    errCompanyNameMin: "O nome da empresa deve ter pelo menos 2 caracteres",
    errCompanyNameMax: "O nome da empresa deve ter menos de 255 caracteres",
    errBillingEmailRequired: "O e-mail de faturamento é obrigatório",
    errEmailInvalid: "Insira um endereço de e-mail válido",
    errBillingAddressRequired: "O endereço de faturamento é obrigatório",
    errBillingAddressMin: "O endereço de faturamento deve ter pelo menos 10 caracteres",
    errBillingAddressMax: "O endereço de faturamento deve ter menos de 500 caracteres",
    errCountryRequired: "O país é obrigatório",
    errCountryInvalid: "Selecione um código de país válido",
    errPhoneRequired: "O número de telefone é obrigatório",
    errPhoneMin: "O número de telefone deve ter pelo menos 10 caracteres",
    errPhoneMax: "O número de telefone deve ter menos de 20 caracteres",
    adminTitle: "Criar conta de administrador",
    adminSubtitle: "Configure sua conta de administrador para acessar o wxKanban. Este é o primeiro usuário que será criado para sua empresa.",
    emailLabel: "Endereço de e-mail *",
    firstNameLabel: "Nome *",
    lastNameLabel: "Sobrenome (opcional)",
    passwordLabel: "Senha *",
    pwReqTitle: "Requisitos da senha:",
    pwReqLength: "Pelo menos 12 caracteres",
    pwReqUpper: "Pelo menos uma letra maiúscula",
    pwReqLower: "Pelo menos uma letra minúscula",
    pwReqNumber: "Pelo menos um número",
    pwReqSpecial: "Pelo menos um caractere especial (!@#$%^&*)",
    termsPrefix: "Ao criar uma conta, você concorda com nossos",
    termsOfService: "Termos de Serviço",
    and: "e",
    privacyPolicy: "Política de Privacidade",
    creatingAccount: "Criando conta...",
    completeRegistration: "Concluir cadastro",
    errEmailRequired: "O e-mail é obrigatório",
    errPasswordRequired: "A senha é obrigatória",
    errPasswordRequirements: "A senha não atende a todos os requisitos",
    errFirstNameRequired: "O nome é obrigatório",
    errFirstNameLength: "O nome deve ter entre 1 e 100 caracteres",
    errLastNameLength: "O sobrenome deve ter entre 1 e 100 caracteres",
    paymentMethod: "Forma de pagamento",
    preparingPayment: "Preparando o pagamento seguro…",
    initPaymentError: "Falha ao inicializar o pagamento",
    subscribingTo: "Você está assinando",
    at: "por",
    perMonth: "/mês",
    stripeNotice: "Os dados do cartão são tratados diretamente pelo Stripe. Nenhum dado sensível passa pelos nossos servidores.",
    paymentFailedCard: "O pagamento falhou. Verifique seu cartão e tente novamente.",
    paymentFailed: "O pagamento falhou",
    processing: "Processando…",
    completePayment: "Concluir pagamento",
    startPromo: "Iniciar período gratuito",
    inviteChecking: "Verificando seu convite…",
    inviteInvalid: "Este link de convite não é válido. Ele pode já ter sido usado, revogado ou expirado.",
    inviteFreeMonths: "Você foi convidado: seus primeiros {{days}} dias no {{plan}} são gratuitos.",
    inviteBoundTo: "Este convite é para {{email}} — você se registrará com esse endereço.",
    promoFreeForDays: "Seus primeiros {{days}} dias são gratuitos.",
    promoFirstChargeOn: "Seu primeiro pagamento de {{amount}} será em {{date}}.",
    promoFirstChargeAfter: "Seu primeiro pagamento de {{amount}} será após {{days}} dias.",
    promoCardNotChargedToday: "Seu cartão não será cobrado hoje. Nós o mantemos em arquivo para que sua assinatura continue automaticamente.",
    tierSingleUser: "Usuário único",
    tierSmallBusiness: "Pequena/Média empresa",
    tierConsultant: "Consultor",
    tierEnterprise: "Enterprise",
    tierFree: "Grátis",
    confTitle: "Confirme seu cadastro",
    confSubtitle: "Revise suas informações antes de concluir o cadastro",
    confCompanyDesc: "Detalhes sobre sua organização",
    confCompanyName: "Nome da empresa",
    confBillingEmail: "E-mail de faturamento",
    subscriptionPlan: "Plano de assinatura",
    subscriptionPlanDesc: "Seu nível e preço selecionados",
    selectedPlan: "Plano selecionado",
    monthlyPrice: "Preço mensal",
    adminAccount: "Conta de administrador",
    adminAccountDesc: "Seus dados de login de administrador",
    nameLabel: "Nome",
    emailShort: "E-mail",
    termsClickPrefix: "Ao clicar em «Concluir cadastro», você concorda com nossos",
    title: "Bem-vindo ao wxKanban",
    settingUp: "Configurando sua conta",
    account: "",
    stepCompany: "Empresa",
    stepPayment: "Pagamento",
    stepAdmin: "Admin",
    successTitle: "Empresa criada com sucesso!",
    verificationSent: "Um e-mail de verificação foi enviado para",
    whatsNext: "Próximos passos:",
    next1: "Verifique seu e-mail e clique no link de verificação",
    next2: "Entre na sua nova conta wxKanban",
    next3: "Conclua a configuração da equipe e comece a gerenciar projetos",
    goToLogin: "Ir para o login",
    createAnother: "Criar outra conta",
    haveAccount: "Já tem uma conta?",
    signIn: "Entrar",
  },
  settingsPage: {
    title: "Configurações",
    subtitle: "Gerencie as configurações da sua empresa e conta",
    tabCompany: "Empresa",
    tabProfile: "Perfil",
    tabNotifications: "Notificações",
    tabSecurity: "Segurança",
    companyInfo: "Informações da empresa",
    companyInfoDesc: "Atualize os detalhes e as informações de cobrança da sua empresa",
    profileInfo: "Informações do perfil",
    profileInfoDesc: "Atualize suas informações pessoais e preferências",
    language: "Idioma",
    changePassword: "Alterar senha",
    notificationPrefs: "Preferências de notificação",
    securitySettings: "Configurações de segurança",
    saveChanges: "Salvar alterações",
    saveProfile: "Salvar perfil",
  },
  dashboard: {
    failedToFetch: "Falha ao obter: {{status}}",
    failedToLoadPendingTasks: "Falha ao carregar as tarefas pendentes",
    adminTitle: "Painel de administração",
    lastUpdated: "Última atualização: {{time}}",
    welcome: "Bem-vindo, {{email}}",
    panelCompany: "Empresa",
    totalUsers: "Total de usuários",
    activeUsers: "Usuários ativos",
    byRole: "Por função:",
    panelProjects: "Projetos",
    totalProjects: "Total de projetos",
    active: "Ativos",
    panelTeamSummary: "Resumo da equipe",
    total: "Total",
    panelFinancials: "Finanças",
    revenue: "Receita (este mês)",
    invoices: "Faturas",
    outstanding: "Pendentes",
    panelActivity: "Atividades",
    loadingActivities: "Carregando atividades…",
    noActivity: "Nenhuma atividade recente",
    panelPendingTasks: "Tarefas pendentes",
    loading: "Carregando…",
    noActiveProjects: "Nenhum projeto ativo.",
    colProject: "Projeto",
    colOpen: "Abertas",
    colInProgress: "Em and.",
  },
  projects: {
    title: "Projetos",
    noProjects: "Nenhum projeto ainda",
    count: "{{count}} projetos",
    countOne: "1 projeto",
    showAll: "Mostrar todos",
    myProjects: "Meus projetos",
    newProject: "Novo projeto",
    searchPlaceholder: "Buscar projetos…",
    sort: "Ordenar:",
    sortUpdated: "Atualizado",
    sortCreated: "Criado",
    sortName: "Nome",
    loadError: "Não foi possível carregar os projetos",
    noMatch: "Nenhum projeto corresponde aos filtros atuais",
    clearFilters: "Limpar filtros",
    createFirst: "Crie seu primeiro projeto",
  },
  tasks: {
    back: "Voltar ao painel",
    title: "Minhas tarefas",
    lastSync: "Última sincronização:",
    syncNow: "Sincronizar",
    syncing: "Sincronizando…",
    allSources: "Todas as fontes",
    allPriorities: "Todas as prioridades",
    priorityCritical: "Crítica",
    priorityHigh: "Alta",
    priorityMedium: "Média",
    priorityLow: "Baixa",
    allStatuses: "Todos os status",
    statusOpen: "Aberta",
    statusInProgress: "Em andamento",
    statusCompleted: "Concluída",
    statusOrphaned: "Órfã",
    sortUpdated: "Atualizadas recentemente",
    sortDue: "Data de vencimento",
    sortPriority: "Prioridade",
    sortTitle: "Título",
    loading: "Carregando tarefas…",
    noTasks: "Nenhuma tarefa ainda",
    connectHint1: "Conecte um sistema PM em",
    integrations: "Integrações",
    connectHint2: "e clique em Sincronizar para importar suas tarefas atribuídas.",
  },
  integrations: {
    title: "Integrações de sistemas de gestão de projetos",
    subtitle: "Conecte suas ferramentas de gestão de projetos ao wxKanban",
    viewTasks: "Ver minhas tarefas →",
    connectedSuccess: "✓ Sistema de gestão de projetos conectado com sucesso!",
    loading: "Carregando integrações...",
    connectedSince: "Conectado desde {{date}}",
    notConnected: "Não conectado",
    active: "✓ Ativo",
    disconnect: "Desconectar",
    connect: "Conectar",
    needHelp: "Precisa de ajuda para conectar?",
    helpText: "Cada sistema de gestão de projetos requer credenciais OAuth. Consulte a documentação para obter instruções de configuração.",
    confirmDisconnect: "Desconectar {{system}}?",
  },
  invoices: {
    title: "Faturas",
    subtitle: "Gere e gerencie faturas de clientes a partir de registros de tempo faturáveis",
    generate: "Gerar fatura",
    statTotal: "Total de faturas",
    statRevenue: "Receita total",
    statSent: "Faturas enviadas",
    modalTitle: "Gerar nova fatura",
    modalDesc: "Selecione o intervalo de datas para os registros de tempo faturáveis",
    startDate: "Data de início",
    endDate: "Data de término",
    generateConfirm: "Gerar",
    history: "Histórico de faturas",
    emptyTitle: "Nenhuma fatura gerada ainda",
    emptyHint: "Crie sua primeira fatura para começar",
    colNumber: "N.º da fatura",
    colDate: "Data",
    colBilledTo: "Faturado para",
    colHours: "Horas",
    colAmount: "Valor",
    colStatus: "Status",
    colActions: "Ações",
    statusSent: "Enviada",
    statusDraft: "Rascunho",
    na: "N/D",
    detailTitle: "Fatura {{number}}",
    generatedOn: "Gerada em {{date}}",
    hoursWorked: "Horas trabalhadas",
    totalAmount: "Valor total",
    downloadPdf: "Baixar PDF",
    sendInvoice: "Enviar fatura",
    resend: "Reenviar",
  },
  reports: {
    title: "Relatórios",
    subtitle: "Veja análises detalhadas sobre controle de tempo, projetos e desempenho da equipe",
    tabTime: "Relatórios de tempo",
    tabProjects: "Relatórios de projetos",
    dateRange: "Intervalo de datas",
    exportCsv: "Exportar CSV",
    timeByMember: "Tempo por membro da equipe",
    timeByMemberDesc: "Detalhamento das horas registradas por cada membro da equipe",
    selectDateRange: "Selecione um intervalo de datas para ver os relatórios de tempo",
    colMember: "Membro da equipe",
    colTotalHours: "Horas totais",
    colBillableHours: "Horas faturáveis",
    colBillablePct: "% faturável",
    projectPerformance: "Desempenho dos projetos",
    projectPerformanceDesc: "Visão geral de todos os projetos e seu progresso",
    noProjects: "Nenhum projeto encontrado",
    colProject: "Projeto",
    colHours: "Horas",
    colTasks: "Tarefas",
    colCompletion: "Conclusão",
    colTeamSize: "Tamanho da equipe",
  },
  workhub: {
    summaryTotal: "Tarefas totais",
    summaryPastDue: "Vencidas",
    summaryAging: "Mais de 30 dias",
    changeStatus: "Alterar status da tarefa",
    task: "Tarefa",
    newStatus: "Novo status",
    blockedDesc: "uma dependência externa impede o progresso.",
    suspendedDesc: "pausada para reavaliação.",
    reason: "Motivo",
    reasonPlaceholder: "Explique por que esta tarefa está sendo pausada...",
    reasonRequired: "É necessário um motivo para tarefas bloqueadas ou suspensas.",
    updateStateError: "Falha ao atualizar o status da tarefa",
    unknownError: "Erro desconhecido",
    submit: "Enviar",
    saving: "Salvando...",
    cancel: "Cancelar",
    saveTimeEntry: "Salvar registro de tempo",
    duration: "Duração",
    billableQ: "Faturável?",
    billable: "Faturável",
    nonBillable: "Não faturável",
    notesOptional: "Notas (opcional)",
    notesPlaceholder: "No que você trabalhou?",
    saveEntry: "Salvar registro",
    saveEntryError: "Falha ao salvar o registro",
    taskActivity: "Atividade da tarefa",
    loadingActivity: "Carregando a atividade da tarefa...",
    loadDetailError: "Falha ao carregar os detalhes da tarefa",
    timeEntries: "Registros de tempo ({{count}})",
    noTimeEntries: "Ainda não há registros de tempo.",
    invoiced: "Faturado",
    statusHistory: "Histórico de status ({{count}})",
    noStateChanges: "Nenhuma alteração de status registrada.",
    pmSyncState: "Estado de sincronização do PM",
    syncStatus: "Status:",
    syncUnknown: "desconhecido",
    lastSyncLabel: "Última sincronização: {{time}}",
    retries: "Tentativas: {{count}}",
    errorLabel: "Erro: {{message}}",
    resolved: "Resolvido: {{time}}",
    aiBadge: "IA",
    manualBadge: "Manual",
    running: "em andamento",
    title: "Central de trabalho do consultor",
    subtitle: "Fila de tarefas unificada com filtragem por projeto e controle de tempo com um clique.",
    allProjects: "Todos os projetos",
    timerRunning: "Cronômetro em execução",
    stopTimer: "Parar cronômetro",
    inactivityTitle: "O cronômetro está em execução há mais de 8 horas",
    inactivityBody: "Ainda é trabalho ativo? Pare o cronômetro se você se ausentou.",
    keepRunning: "Manter em execução",
    loadingQueue: "Carregando a fila do consultor...",
    loadError: "Falha ao carregar os dados da central de trabalho.",
    assignedQueue: "Fila de trabalho atribuída",
    noTasks: "Nenhuma tarefa atribuída encontrada para este filtro.",
    compliance: "Conformidade",
    activity: "Atividade",
    blocked: "Bloqueada",
    suspended: "Suspensa",
    stop: "Parar",
    startTimer: "Iniciar cronômetro",
    activeTask: "Tarefa ativa",
    billablePreview: "Prévia faturável pronta para faturamento",
    complianceFlaggedTasks: "Tarefas sinalizadas para conformidade: {{count}}",
    loadingPreview: "Carregando prévia faturável...",
    previewError: "Não foi possível carregar a prévia faturável.",
    previewSummary: "Entradas: {{count}} • Total de horas faturáveis: {{hours}}",
    sourceBreakdown: "Detalhamento por origem:",
    manual: "Manual:",
    ai: "IA:",
    complianceReviewPanel: "Painel de revisão de conformidade",
    loadingFlags: "Carregando sinalizações de conformidade...",
    flagsError: "Não foi possível carregar as sinalizações de conformidade.",
    noFlags: "Nenhuma sinalização de conformidade aberta.",
  },
  billing: {
    title: "Configuração de faturamento",
    subtitle: "Configure as taxas por hora para controle de tempo e faturamento. As taxas seguem uma hierarquia: Usuário → Projeto → Padrão da empresa.",
    fetchError: "Falha ao obter as taxas de faturamento",
    updateError: "Falha ao atualizar a taxa",
    updateSuccess: "Taxa de faturamento atualizada com sucesso",
    invalidRate: "Insira uma taxa válida",
    resolutionTitle: "Ordem de resolução de taxas:",
    resolutionBody: "Ao calcular os valores faturáveis, o sistema verifica as taxas nesta ordem: 1) taxa específica do usuário, 2) taxa específica do projeto, 3) taxa padrão da empresa.",
    companyRate: "Taxa padrão da empresa",
    companyRateDesc: "Taxa de reserva usada quando nenhuma taxa de usuário ou projeto está definida",
    hourlyRate: "Taxa por hora (USD)",
    projectRates: "Taxas específicas de projeto",
    projectRatesDesc: "Substituir o padrão da empresa para projetos específicos",
    projectIdLabel: "ID do projeto: {{id}}",
    userRates: "Taxas específicas de usuário",
    userRatesDesc: "Substituir todas as outras taxas para membros específicos da equipe",
    noTeamMembers: "Nenhum membro da equipe encontrado",
    customRate: "Taxa personalizada",
    usingDefault: "Usando padrão",
    defaultPlaceholder: "Padrão",
  },
  verification: {
    noToken: "Nenhum token de verificação fornecido. Verifique o link do seu e-mail.",
    verifiedSuccess: "Seu e-mail foi verificado com sucesso!",
    verifyFailed: "Falha ao verificar o e-mail. O link pode ter expirado.",
    unexpectedError: "Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte.",
    resendFailed: "Falha ao reenviar o e-mail de verificação. Tente novamente.",
    networkError: "Erro de rede. Tente novamente.",
    titleLoading: "Verificando seu e-mail...",
    titleSuccess: "E-mail verificado!",
    titleError: "Falha na verificação",
    descLoading: "Aguarde enquanto verificamos seu endereço de e-mail",
    descSuccess: "Agora você pode entrar na sua conta",
    descError: "Encontramos um problema com sua verificação",
    signInNow: "Entrar agora",
    resending: "Reenviando...",
    resendButton: "Reenviar e-mail de verificação",
    resentNotice: "E-mail de verificação reenviado! Verifique sua caixa de entrada.",
    backToSignIn: "Voltar para o login",
    redirecting: "Redirecionando para o login em 3 segundos...",
  },
  invitePage: {
    errorNoToken: "Nenhum token de convite fornecido",
    errorInvalidExpired: "Convite inválido ou expirado",
    errorPasswordMismatch: "As senhas não coincidem",
    errorPasswordTooShort: "A senha deve ter pelo menos 8 caracteres",
    errorAcceptFailed: "Falha ao aceitar o convite",
    errorUnexpected: "Ocorreu um erro inesperado",
    invalidTitle: "Convite inválido",
    invalidDescription: "Este link de convite é inválido ou expirou.",
    invalidContactPrompt: "Entre em contato com a pessoa que o convidou para solicitar um novo convite.",
    goToSignIn: "Ir para o login",
    acceptedTitle: "Bem-vindo à equipe!",
    acceptedDescription: "Sua conta foi criada com sucesso.",
    acceptedBody: "Agora você pode fazer login para acessar o espaço de trabalho wxKanban da sua empresa.",
    signInNow: "Entrar agora",
    joinTitle: "Junte-se a {{companyName}}",
    invitedBy: "Você foi convidado por {{inviterName}} para participar como {{role}}",
    emailLabel: "E-mail",
    firstNameLabel: "Nome",
    lastNameLabel: "Sobrenome",
    passwordLabel: "Senha",
    confirmPasswordLabel: "Confirmar senha",
    creatingAccount: "Criando conta...",
    acceptAndCreate: "Aceitar convite e criar conta",
    alreadyHaveAccount: "Já tem uma conta?",
    signInHere: "Entre aqui",
  },
  inviteAccept: {
    subtitle: "Convite para a equipe",
    validating: "Validando convite...",
    expiredTitle: "Convite expirado",
    expiredBody: "Este link de convite expirou. Peça ao seu administrador para reenviá-lo.",
    usedTitle: "Convite já utilizado",
    invalidTitle: "Convite inválido",
    usedBody: "Este convite já foi aceito. Tente fazer login.",
    invalidBody: "Este link de convite é inválido ou expirou.",
    goToLogin: "Ir para o login",
    successTitle: "Conta criada!",
    successBody: "Sua conta foi configurada. Agora você pode fazer login no wxKanban.",
    logIn: "Entrar",
    joinCompany: "Juntar-se a {{company}}",
    creatingAccountFor: "Criando conta para",
    fullNameLabel: "Nome completo",
    passwordLabel: "Senha",
    passwordPlaceholder: "Mín. 8 caracteres",
    confirmPasswordLabel: "Confirmar senha",
    confirmPasswordPlaceholder: "Digite a senha novamente",
    timezoneLabel: "Fuso horário",
    timezoneHint: "Detectado automaticamente do seu navegador",
    creatingAccount: "Criando conta...",
    createAccount: "Criar conta",
    alreadyHaveAccount: "Já tem uma conta?",
    signIn: "Entrar",
    errorPasswordMismatch: "As senhas não coincidem",
    errorPasswordTooShort: "A senha deve ter pelo menos 8 caracteres",
    errorCreateAccountFailed: "Falha ao criar a conta",
    errorUnknown: "Erro desconhecido",
  },
  inviteTeam: {
    title: "Convidar membros da equipe",
    subtitle: "Adicione colegas para colaborar em seus projetos",
    sendInvitations: "Enviar convites",
    sendInvitationsDesc: "Escolha como deseja convidar os membros da sua equipe",
    singleInvite: "Convite individual",
    bulkUpload: "Upload em massa",
    sendMore: "Enviar mais convites",
    viewTeam: "Ver equipe",
    tipsTitle: "Dicas para convidar membros da equipe",
    tipEmails: "Certifique-se de usar os endereços de e-mail corretos para evitar devoluções",
    tipRoles: "Escolha as funções adequadas: Administrador (acesso total), Gerente (pode gerenciar projetos) ou Membro (pode visualizar e concluir tarefas)",
    tipCsv: "Para uploads em massa, use um arquivo CSV com as colunas: email, usertype, firstName, lastName",
    tipExpiry: "Os convites expiram após 7 dias se não forem aceitos",
  },
  teamDashboard: {
    title: "Meu painel",
    welcomeBack: "Bem-vindo de volta, {{email}}",
    assignedProjects: "Projetos atribuídos",
    openTasks: "Tarefas abertas",
    hoursThisWeek: "Horas esta semana",
    noProjectsAssigned: "Nenhum projeto atribuído a você ainda.",
    workHubTitle: "Central de trabalho do consultor",
    workHubDescription: "Abra sua fila de tarefas unificada e registre o tempo diretamente no trabalho atribuído.",
    openWorkHub: "Abrir central de trabalho",
  },
  distributorSignup: {
    errorPasswordsMismatch: "As senhas não coincidem",
    errorPasswordTooShort: "A senha deve ter pelo menos 8 caracteres",
    errorAcceptTerms: "Aceite os termos de distribuidor",
    errorRegistrationFailed: "Falha no registro",
    errorUnknown: "Erro desconhecido",
    successHeading: "Tudo pronto",
    successSignedInPrefix: "Sua conta gratuita de distribuidor está pronta. Você entrou como",
    referralCodeLabel: "Seu código de indicação",
    copyCodeTitle: "Copiar código",
    referralCodeHelp: "Compartilhe este código com as pessoas que você indica. Quando elas o inserem ao se cadastrar, a conta é creditada a você.",
    goToDashboard: "Ir para o seu painel",
    introBannerBefore: "Torne-se distribuidor do wxKanban. Configure sua conta",
    introBannerFree: "gratuita",
    introBannerAfter: "abaixo — você receberá um código de indicação para compartilhar e entrará automaticamente ao concluir.",
    fieldBusinessName: "Nome da empresa *",
    fieldFullName: "Seu nome completo *",
    fieldEmail: "E-mail *",
    fieldPassword: "Senha *",
    placeholderPassword: "Pelo menos 8 caracteres",
    fieldConfirmPassword: "Confirmar senha *",
    placeholderConfirmPassword: "Digite a senha novamente",
    acceptTermsLabel: "Aceito os termos de distribuidor. Minha conta é uma conta gratuita de usuário único — não estou inscrito em um plano pago.",
    creatingAccount: "Criando conta…",
    createAccountButton: "Criar conta gratuita",
    pageSubtitle: "Cadastro de distribuidor",
  },
  overviewPage: {
    loadFailed: "Falha ao carregar o documento de visão geral ({{status}})",
    unknownError: "Erro desconhecido",
    dashboard: "Painel",
    projectOverview: "Visão geral do projeto",
    refresh: "Atualizar",
    loadErrorTitle: "Não foi possível carregar o documento de visão geral",
  },
  projectWorkspace: {
    // [SCOPE 137 / T019] Change Impact mode (owner-set)
    impactModeTitle: "Modo de impacto de mudanças",
    impactModeHelp: "Como a verificação de impacto do kit trata as edições de IA neste projeto. Vale para o kit de cada desenvolvedor e prevalece sobre um valor local.",
    impactModeNotify: "Notificar: dizer ao desenvolvedor o que uma edição afeta, sem nunca perguntar",
    impactModeBoundaries: "Limites: perguntar só quando uma edição alcança outro escopo ou um teste aprovado (padrão)",
    impactModeAlways: "Sempre: perguntar sempre que uma edição afetar qualquer outra coisa",
    impactModeNotSet: "Não definido aqui. O kit de cada desenvolvedor usa seu valor local, ou Limites.",
    impactModeSetBy: "Definido por {{who}} em {{when}}",
    impactModeOwnerOnly: "Somente o dono do projeto ou um administrador da empresa pode alterar isto.",
    impactModeDataLoss: "Uma possível perda de dados é interrompida em todos os modos.",
    impactModeError: "A alteração não foi salva. Tente novamente.",
    complianceTabSettings: "Configurações",
    complianceTabDocuments: "Documentos",
    complianceTabTasks: "Tarefas",
    complianceTabAudit: "Auditoria",
    complianceTabReports: "Relatórios",
    learningsGenerateFailed: "Falhou ({{status}})",
    learningsPrintHint: "Use a impressão do navegador para salvar como PDF",
    regenerate: "Regenerar",
    learningsIframeTitle: "Aprendizados do projeto",
    learningsNoneTitle: "Ainda não há relatório de aprendizados",
    learningsRunPrefix: "Execute",
    learningsRunSuffix: "após concluir o trabalho para capturar decisões, correções e controles de conformidade.",
    learningsThenGenerate: "Em seguida, gere o relatório abaixo.",
    generating: "Gerando…",
    generateLearningsReport: "Gerar relatório de aprendizados",
    errFailedLoadProject: "Falha ao carregar o projeto",
    confirmArchive: "Arquivar este projeto? Ele será ocultado da lista de projetos.",
    confirmDelete: "Excluir permanentemente \"{{name}}\"? Isso remove o projeto e todas as suas tarefas, fases, specs e documentos. Isso não pode ser desfeito.",
    thisProject: "este projeto",
    errFailedDeleteProjectStatus: "Falha ao excluir o projeto ({{status}})",
    errFailedDeleteProject: "Falha ao excluir o projeto",
    errFailedUpdateProject: "Falha ao atualizar o projeto",
    errFailedUpdateAuditType: "Falha ao atualizar o tipo de auditoria",
    loadingProject: "Carregando projeto...",
    projectNotFound: "Projeto não encontrado",
    backToProjects: "Voltar aos projetos",
    projects: "Projetos",
    clientEngagementTitle: "Acesso via engajamento ativo com {{name}}",
    client: "Cliente",
    clientProjectBadge: "Projeto de cliente — {{name}}",
    editProject: "Editar projeto",
    auditLabel: "Auditoria:",
    noAudit: "Sem auditoria",
    archiveProject: "Arquivar projeto",
    archive: "Arquivar",
    deleteProjectTitle: "Excluir projeto permanentemente",
    delete: "Excluir",
    tabLifecycle: "Ciclo de vida",
    tabDevelopmentPlan: "Plano de desenvolvimento",
    tabPortfolioAnalyzer: "Analisador de portfólio",
    tabCompliance: "Conformidade",
    tabLearnings: "Aprendizados",
    tabKit: "Kit",
    views: "Visualizações",
    viewMatrix: "Matriz",
    viewStack: "Stack",
    viewTheme: "Tema",
    viewBugReports: "Relatórios de bugs",
    iframeLifecycle: "Ciclo de vida do projeto",
    iframeDevplan: "Plano de desenvolvimento",
    phaseNotStarted: "Esta fase ainda não começou.",
    saveFailed: "Falha ao salvar",
    saveErrorIntro: "Ocorreu o seguinte erro ao salvar:",
    copyToClipboard: "Copiar para a área de transferência",
    copy: "Copiar",
    copyHint: "Clique em \"Copiar\" para copiar esta mensagem de erro para a área de transferência e reportá-la.",
    close: "Fechar",
    copyAndClose: "Copiar e fechar",
    fieldProjectName: "Nome do projeto",
    fieldDescription: "Descrição",
    fieldHourlyRate: "Taxa horária ($)",
    fieldAllocatedHours: "Horas alocadas",
    fieldPmSystems: "Sistemas PM",
    fieldSecurityClassification: "Classificação de segurança",
    fieldAuditType: "Tipo de auditoria",
    phaseAssignments: "Atribuições de fase",
    loadingTeamMembers: "Carregando membros da equipe...",
    unassigned: "Não atribuído",
    reassignPermissionNote: "Apenas ADMIN e TEAM_MANAGER podem reatribuir fases.",
    cancel: "Cancelar",
    saving: "Salvando...",
    saveChanges: "Salvar alterações",
  },
  helpPage: {
    backToHelpCenter: "Voltar à Central de Ajuda",
    examples: "Exemplos",
    wasArticleHelpful: "Este artigo foi útil?",
    yesItHelped: "Sim, ajudou",
    noNotHelpful: "Não, não ajudou",
    helpCenter: "Central de Ajuda",
    // [SCOPE 138 / T011] "Learn wxKanban with your AI"
    teachTitle: "Aprenda o wxKanban com a sua IA",
    teachHowTo: "Baixe as lições, entregue start-here.md (ou o zip inteiro) a qualquer chat de IA e diga \"me ensine\". Cada lição guia você passo a passo por uma tarefa.",
    teachDownload: "Baixar as lições",
    teachVersion: "wxKanban {{version}} · {{count}} lições",
    teachError: "O download falhou. Tente novamente.",
    subtitle: "Encontre respostas, documentação e guias para o wxKanban",
    searchPlaceholder: "Pesquise artigos de ajuda, comandos CLI ou guias...",
    searchResults: "Resultados da pesquisa",
    searching: "Pesquisando...",
    noResults: "Nenhum resultado encontrado para \"{{query}}\"",
    viewsCount: "{{count}} visualizações",
    topicsCount: "{{count}} tópicos",
    moreCount: "+{{count}} mais",
  },
  planHelp: {
    title: "Ajuda",
    subtitlePrefix: "O que cada área do wxKanban faz e o que o seu plano inclui. Mostrando",
    subtitleSuffix: "por padrão — escolha outro plano para comparar.",
    yourPlan: "seu plano",
    included: "Incluído",
    upgradeRequired: "Upgrade necessário",
    loading: "Carregando seu plano…",
  },
  faqPage: {
    title: "Ajuda e perguntas frequentes",
    subtitle: "Navegue pelas respostas ou faça uma pergunta e nós a adicionaremos às perguntas frequentes.",
    panel: {
      heading: "Perguntas frequentes",
      loading: "Carregando…",
      empty: "Ainda não há perguntas frequentes — faça uma pergunta abaixo.",
      askPrompt: "Não encontrou sua resposta? Pergunte para nós",
      placeholder: "Digite sua pergunta…",
      submit: "Enviar pergunta",
      sending: "Enviando…",
      tooShort: "Digite uma pergunta (pelo menos 5 caracteres).",
      submitError: "Não foi possível enviar sua pergunta.",
      thanks: "Obrigado — vamos responder.",
    },
  },
  aboutPage: {
    title: "Sobre o wxKanban",
    lede: "O wxKanban é uma forma de criar software com IA orientada por especificações — ensina você a trabalhar como os profissionais, levando uma aplicação por todo o ciclo de vida, de uma ideia confusa a um resultado limpo, testado e entregue, sobre bases que realmente lhe pertencem.",
    whatItIsHeading: "O que é",
    whatItIsPara1: "O wxKanban é um ambiente de desenvolvimento orientado por especificações. Você percorre um ciclo de vida guiado de seis fases — Design → Implementação → QA → Testes humanos → Beta → Lançamento — em que nada é construído sem uma especificação aprovada, e cada alteração é rastreável até a especificação que a autorizou. Igualmente importante, ele mantém suas especificações e documentação coerentes à medida que o software evolui: alterações futuras são verificadas em relação ao que já está escrito, de modo que sua documentação é atualizada junto com o trabalho em vez de ficar desatualizada.",
    whatItIsIntro: "Em torno desse ciclo de vida, o wxKanban oferece o que você precisa para realizar um trabalho real em equipe:",
    teamMgmtLabel: "Gestão de equipe",
    teamMgmtBody: "— convide pessoas, defina seus papéis e controle quem pode fazer o quê.",
    checkoutLabel: "Check-out e check-in",
    checkoutBody: "— reserve um escopo para trabalhar nele e devolva-o quando terminar, para que o trabalho permaneça coordenado e ninguém sobrescreva silenciosamente o de outra pessoa.",
    cockpitLabel: "O Cockpit",
    cockpitBody: "— um centro de comando ao vivo para o seu projeto: onde ele está, o que vem a seguir e cada comando do wxKanban em um só lugar.",
    helpLabel: "Ajuda ativa e contextual",
    helpBody: "— orientação e respostas bem onde você trabalha, tanto dentro do wxKanban quanto dentro do VS Code, onde a construção realmente acontece.",
    whatItIsClosing: "Esse é o coração do wxKanban: o ciclo de vida orientado por especificações, com as ferramentas para conduzi-lo em equipe.",
    whyHeading: "Por que existe",
    whyPara1: "A IA mudou a forma como o software é construído, mas a parte difícil nunca foi a sintaxe — foi o ",
    whyEmphasis: "discernimento",
    whyPara2: " para levar uma aplicação até a entrega. O wxKanban foi construído para dar aos desenvolvedores experientes uma forma de levar esse discernimento para a era da IA e para dar aos iniciantes essa disciplina desde o primeiro dia — para que a IA acelere o seu trabalho em vez de fugir do seu controle.",
    groundHeading: "Construído sobre um terreno que é seu",
    groundPara: "Seus dados ficam em um banco de dados SQL padrão, sua autenticação é sua e você pode trocar de provedor de IA quando quiser. Sem dependência proprietária, sem fornecedor se interpondo entre você e o software do qual você depende.",
    behindHeading: "Quem está por trás",
    behindPara1: "O wxKanban é feito pela ",
    behindPara2: ", fundada por Andy “Cowboy” Stapleton — que passou mais de 40 anos atravessando cada grande mudança de plataforma, do mainframe à web e à nuvem, e ajudando outros desenvolvedores a fazer a mesma travessia.",
    contactHeading: "Entre em contato",
    contactIntro: "Perguntas ou ideias? Envie um e-mail para",
    contactOutro: "— nós realmente respondemos.",
    phoneLabel: "Telefone:",
  },
  bioPage: {
    title: "Sobre o fundador",
    photoAlt: "Retrato do fundador",
    loading: "Carregando…",
  },
  privacyPage: {
    title: "Política de privacidade",
    lastUpdated: "Última atualização: {{date}}",
    intro:
      "O wxKanban (“nós”, “wxperts”) respeita a sua privacidade. Esta política explica o que coletamos quando você usa o aplicativo wxKanban, por quê, e quais escolhas você tem. Coletamos o necessário para operar o serviço, não vendemos os seus dados e você pode solicitar que os excluamos.",
    authoritativeNote:
      "Esta política foi traduzida por conveniência. A versão em inglês é a versão oficial.",
    sections: {
      s1heading: "Informações que coletamos",
      s1item1:
        "Dados da conta e da empresa — seu nome, endereço de e-mail, senha (armazenada apenas como um hash seguro), nome da empresa, cargo e preferência de idioma.",
      s1item2:
        "O trabalho que você cria — projetos, tarefas, especificações, notas, registros de tempo, faturas e conteúdo relacionado que você insere no aplicativo.",
      s1item3:
        "Informações de cobrança — os detalhes de assinatura e pagamento são processados pelo nosso provedor de pagamentos, o Stripe. Não armazenamos números completos de cartão; mantemos o seu plano, status e histórico de cobrança.",
      s1item4:
        "Serviços conectados — se você vincular uma ferramenta de gerenciamento de projetos (por exemplo, Jira, Monday.com, Asana, Trello), armazenamos os tokens de acesso necessários para a sincronização, criptografados em repouso.",
      s1item5:
        "Uso e registros — registros técnicos básicos (como ações, carimbos de data/hora e erros) usados para operar, proteger e melhorar o serviço.",
      s2heading: "Como utilizamos",
      s2item1: "Para fornecer e operar o aplicativo — sua conta, projetos, controle de tempo e faturamento.",
      s2item2: "Para processar assinaturas e pagamentos por meio do Stripe.",
      s2item3: "Para sincronizar com as ferramentas de gerenciamento de projetos que você escolher conectar.",
      s2item4: "Para viabilizar os recursos assistidos por IA (veja abaixo).",
      s2item5: "Para proteger o serviço, prevenir abusos e oferecer suporte.",
      s3heading: "Processamento por IA",
      s3body:
        "Alguns recursos enviam o conteúdo que você fornece (por exemplo, especificações ou textos relacionados) a provedores de IA terceirizados — como Google Gemini, OpenAI ou outros que configuramos — exclusivamente para gerar o resultado solicitado. Não usamos o seu conteúdo para treinar os nossos próprios modelos, e os provedores de IA o processam sob os seus próprios termos, na qualidade de nossos prestadores de serviço.",
      s4heading: "Com quem compartilhamos",
      s4body:
        "Não vendemos nem alugamos as suas informações. Nós as compartilhamos apenas com os prestadores de serviço que nos ajudam a operar o wxKanban — por exemplo, hospedagem em nuvem (Amazon Web Services), processamento de pagamentos (Stripe) e os provedores de IA acima — e somente para que possam prestar esses serviços para nós. Podemos divulgar informações se exigido por lei.",
      s5heading: "Segurança",
      s5body:
        "Protegemos os seus dados com criptografia em trânsito e em repouso, senhas com hash e controles de acesso. Nenhum sistema é perfeitamente seguro, mas trabalhamos continuamente para proteger as suas informações.",
      s6heading: "Por quanto tempo mantemos",
      s6body:
        "Mantemos a sua conta e o conteúdo que você cria enquanto a sua conta estiver ativa, e por um período razoável depois disso, conforme necessário para fins legais, contábeis e operacionais. Você pode solicitar a exclusão dos seus dados a qualquer momento.",
      s7heading: "Suas escolhas e direitos",
      s7body:
        "Você pode acessar, corrigir, exportar ou excluir as suas informações pessoais e retirar o consentimento quando aplicável. Se você estiver em uma região com direitos específicos de proteção de dados (como a UE ou o Reino Unido, sob o GDPR, ou as leis estaduais de privacidade dos EUA), esses direitos se aplicam a você. Para exercê-los, envie um e-mail para",
      s8heading: "Transferências internacionais",
      s8body:
        "O wxKanban é operado a partir dos Estados Unidos, e as suas informações podem ser processadas lá. Ao usar o serviço, você concorda com essa transferência, sujeita às proteções descritas nesta política.",
      s9heading: "Contato",
      s9body: "Dúvidas sobre privacidade? Envie um e-mail para",
    },
  },
  termsPage: {
    title: "Termos de uso",
    lastUpdated: "Última atualização: {{date}}",
    intro:
      "Estes termos regem o uso do aplicativo e do site do wxKanban. Ao criar uma conta ou usar o serviço, você concorda com eles. Leia também a nossa",
    authoritativeNote:
      "Estes termos foram traduzidos por conveniência. A versão em inglês é a versão oficial.",
    sections: {
      s1heading: "Sua conta",
      s1body:
        "Você é responsável pela exatidão dos dados da sua conta e por manter as suas credenciais seguras. Você é responsável pela atividade que ocorre na sua conta e pelos membros da equipe que você convida.",
      s2heading: "Uso aceitável",
      s2body:
        "Use o wxKanban de forma lícita. Não tente interromper o serviço nem obter acesso não autorizado a ele, usar indevidamente os dados de outros usuários ou usar a plataforma para criar ou armazenar conteúdo ilícito.",
      s3heading: "Assinaturas e cobrança",
      s3body:
        "Os planos pagos são cobrados por meio do Stripe de forma recorrente até o cancelamento. As taxas são cobradas antecipadamente para cada período de cobrança e não são reembolsáveis, exceto quando exigido por lei. Você pode cancelar a qualquer momento; o seu plano permanece ativo até o fim do período atual. Daremos aviso razoável sobre alterações de preço.",
      s4heading: "Seu conteúdo e propriedade",
      s4body:
        "Você é o proprietário dos dados e do conteúdo que cria no wxKanban. Você nos concede a licença limitada necessária para hospedar, processar, fazer backup e exibir esse conteúdo, para que possamos fornecer o serviço. Você pode exportar ou excluir o seu conteúdo conforme descrito na nossa Política de privacidade.",
      s5heading: "Conteúdo gerado por IA",
      s5body:
        "O wxKanban usa IA para auxiliar o seu trabalho. O resultado da IA pode ser imperfeito — você é responsável por revisar, testar e aprovar qualquer conteúdo gerado antes de confiar nele. Não garantimos que os resultados assistidos por IA sejam precisos, completos ou adequados a um propósito específico.",
      s6heading: "Nossa propriedade intelectual",
      s6body:
        "O aplicativo wxKanban, o seu design e os nomes wxKanban e wxperts pertencem à wxperts ou aos seus licenciadores. Estes termos não concedem a você qualquer direito sobre o nosso software ou marca além do uso do serviço conforme previsto.",
      s7heading: "Sem garantias",
      s7body:
        "O serviço é fornecido “no estado em que se encontra” e “conforme disponível”, sem garantias de qualquer tipo. Trabalhamos para manter o wxKanban confiável e disponível, mas não garantimos operação ininterrupta ou livre de erros.",
      s8heading: "Limitação de responsabilidade",
      s8body:
        "Na máxima extensão permitida por lei, a wxperts não se responsabiliza por perdas indiretas, incidentais ou consequenciais, nem por perda de dados ou lucros, decorrentes do seu uso — ou da impossibilidade de uso — do serviço.",
      s9heading: "Rescisão",
      s9body:
        "Você pode encerrar a sua conta a qualquer momento. Podemos suspender ou encerrar o acesso se estes termos forem violados ou para proteger o serviço. Na rescisão, as disposições que, por sua natureza, devam permanecer em vigor continuarão a se aplicar.",
      s10heading: "Lei aplicável",
      s10body:
        "A wxperts opera a partir dos Estados Unidos, e estes termos são regidos pela lei dos EUA, sem consideração aos princípios de conflito de leis. Se você usar o serviço a partir da União Europeia, nada nestes termos remove os direitos obrigatórios de proteção do consumidor e de dados que você tem sob a lei da UE (incluindo o GDPR); essas proteções continuam a se aplicar a você.",
      s11heading: "Alterações",
      s11body:
        "Podemos atualizar estes termos à medida que o produto evolui. A data de “última atualização” acima mostra quando eles mudaram pela última vez; o uso continuado após uma alteração significa que você a aceita.",
      s12heading: "Contato",
      s12body: "Dúvidas sobre estes termos? Envie um e-mail para",
    },
  },
  adminDashboard: {
    saveFailed: "Falha ao salvar",
    editAuditType: "Editar tipo de auditoria",
    complianceFramework: "Framework de conformidade",
    none: "— Nenhum —",
    title: "Painel de administração",
    welcomeBack: "Bem-vindo de volta, {{email}}",
    teamMembers: "Membros da equipe",
    activeProjects: "Projetos ativos",
    hoursThisWeek: "Horas esta semana",
    timeBilling: "Tempo e faturamento",
    timeBillingDesc: "Controle o tempo, defina as taxas de faturamento e gerencie as horas faturáveis em todos os projetos.",
    timeBillingPlaceholder: "As entradas de tempo e as taxas de faturamento aparecerão aqui.",
    systemHealth: "Integridade do sistema",
    reportsAnalytics: "Relatórios e análises",
    reportsDesc: "Resumos de receita, horas por projeto, rentabilidade e relatórios exportáveis.",
    reports: "Relatórios",
    reportsPlaceholder: "Os relatórios financeiros aparecerão aqui assim que as entradas de tempo forem registradas.",
  },
  subscriptionCard: {
    statusActive: "Ativo",
    statusPending: "Pendente",
    statusSuspended: "Suspenso",
    currentPlan: "Plano atual",
    teamSeats: "Assentos da equipe",
    seatLimitReached: "Limite de assentos atingido — faça upgrade para convidar mais membros.",
    upgradePlan: "Fazer upgrade do plano",
  },
  engagementsWidget: {
    title: "Colaborações",
    clientFirms: "{{count}} consultoria nos seus projetos",
    clientFirmsPlural: "{{count}} consultorias nos seus projetos",
    clientsServed: "{{count}} cliente que você atende",
    clientsServedPlural: "{{count}} clientes que você atende",
    viewAll: "Ver tudo",
    roleHiring: "Contratando",
    roleServing: "Atendendo",
    moreCount: "+ {{count}} mais",
  },
  activityFeed: {
    title: "Feed de atividades",
    loading: "Carregando atividade recente...",
    subtitle: "Atividade recente da equipe e dos projetos",
    viewAll: "Ver tudo",
    empty: "Nenhuma atividade recente",
    emptyHint: "A atividade aparecerá aqui à medida que sua equipe trabalha",
    byUser: "por {{name}}",
    justNow: "agora mesmo",
    minutesAgo: "há {{count}} min",
    hoursAgo: "há {{count}} h",
    daysAgo: "há {{count}} d",
  },
  dailyDigest: {
    title: "Resumo diário",
    loadingActivity: "Carregando a atividade de hoje...",
    subtitle: "Resumo da atividade da equipe de hoje",
    configure: "Configurar",
    viewAll: "Ver tudo",
    noActivity: "Ainda não há atividade hoje",
    activityWillAppear: "A atividade da equipe aparecerá aqui",
    byUser: "por {{name}}",
    moreItems: "+{{count}} outros itens",
    justNow: "agora mesmo",
    minutesAgo: "há {{count}} min",
    hoursAgo: "há {{count}} h",
  },
  roleLock: {
    contactAdmin: "Entre em contato com o seu administrador para acessar esta seção.",
    requiresAdmin: "Requer administrador",
  },
  upgradeLock: {
    requiresPlan: "Requer o plano {{plan}}",
    upgradePlan: "Atualizar plano",
  },
  projectProposals: {
    errorFindDraft: "Não foi possível encontrar o rascunho de visão geral para esta proposta",
    errorOpenEditor: "Falha ao abrir o editor",
    errorFetch: "Falha ao buscar as propostas: {{status}}",
    errorLoad: "Falha ao carregar as propostas",
    errorUpdateStatus: "Falha ao atualizar o status",
    rejectionReasonPrompt: "Motivo da rejeição (obrigatório):",
    title: "Propostas de projeto",
    addProposal: "Adicionar proposta",
    loadingProposals: "Carregando propostas...",
    empty: "Ainda não há propostas. Adicione uma para começar.",
    status_pending: "Pendente",
    status_good_idea: "Boa ideia",
    status_accepted: "Aceita",
    status_rejected: "Rejeitada",
    editTitle: "Abrir o editor de visão geral para esta proposta",
    goodIdea: "Boa ideia",
    reject: "Rejeitar",
    accept: "Aceitar",
    createProject: "Criar projeto",
    reopen: "Reabrir",
    projectCreated: "Projeto criado",
  },
  systemHealth: {
    title: "Estado do sistema",
    checked: "Verificado {{time}}",
    refresh: "Atualizar",
    runningDiagnostics: "Executando diagnósticos…",
    allSystemsOperational: "Todos os sistemas operacionais",
    driftOrMissingDetected: "Desvio de esquema ou tabelas ausentes detectados",
    databaseUnreachable: "Base de dados inacessível",
    database: "Base de dados",
    status: "Estado",
    latency: "Latência",
    errorLabel: "Erro",
    tableRows: "linhas {{table}}",
    server: "Servidor",
    uptime: "Tempo ativo",
    memory: "Memória",
    env: "Ambiente",
    diagTime: "Tempo de diag",
    pmIntegrationsConnected: "integrações PM conectadas",
    pmIntegrationConnected: "integração PM conectada",
    schemaDriftTables: "Desvio de esquema — {{count}} tabelas",
    schemaDriftTable: "Desvio de esquema — {{count}} tabela",
    missing: "ausente",
    run: "Execute",
    toFix: "para corrigir.",
    missingTables: "Tabelas ausentes ({{count}})",
    toCreateMissingTables: "para criar as tabelas ausentes.",
  },
  projectGrid: {
    opening: "Abrindo…",
    lastUpdated: "Última atualização: {{date}}",
    notCreated: "não criado",
    noAudit: "Sem auditoria",
    editAuditType: "Editar tipo de auditoria",
    outstandingTasks: "Tarefas pendentes",
    complete: "Concluído",
    inProgress: "Em andamento",
    taskProgress: "{{total}} tarefas - {{completed}} concluídas",
    notStarted: "Não iniciado",
    failedOpenOverview: "Falha ao abrir o editor de visão geral",
    failedOpenOverviewStatus: "Falha ao abrir o editor de visão geral ({{status}})",
    titleProjectOverview: "Visão geral do projeto",
    titleProjectLifecycle: "Lifecycle do projeto",
    titleDevelopmentPlan: "Plano de desenvolvimento",
    titleAuditReport: "Relatório de auditoria",
    titleCompoundLearnings: "Aprendizados acumulados",
    titleProjectScopeAnalysis: "Análise de Scope do projeto",
    titleEntityDiagram: "Diagrama de entidades",
    showDocuments: "Mostrar documentos",
    hideDocuments: "Ocultar documentos",
    newProject: "Novo",
    allProjects: "Todos os projetos",
    active: "Ativos",
    branched: "Ramificados",
    emptyAdmin: "Ainda não há projetos ativos. Clique em Novo para criar seu primeiro projeto.",
    emptyTeam: "Nenhum projeto atribuído a você ainda.",
  },
  phaseTabBar: {
    assignedUser: "Usuário atribuído",
  },
  designPhase: {
    specifications: "Especificações",
    clarifiedCount: "{{clarified}} / {{total}} esclarecidas",
  },
  implementationPhase: {
    tasksHeading: "Tarefas",
    openCount: "{{n}} aberta(s)",
  },
  regression: {
    loading: "Comparando com execuções anteriores…",
    failed: "Não foi possível montar a comparação",
    title: "Verificação de regressão para {{version}}",
    blocking: "{{count}} bloqueando",
    newlyFailing: "Começou a falhar",
    newlyFailingHint: "Passou na última vez que rodou. É isto que impede a publicação.",
    neverPassed: "Nunca passou",
    neverPassedHint: "Trabalho pendente, não uma regressão.",
    stillPassing: "Continua passando",
    lastPassed: "Passou pela última vez em {{version}}",
  },
  testSuite: {
    skipTitle: "Exigência de testes",
    skipOn: "Os testes estão DESLIGADOS para este projeto.",
    skipOff: "Os testes são obrigatórios neste projeto.",
    skipTurnOn: "Pular testes",
    skipReEnable: "Exigir testes novamente",
    skipCancel: "Cancelar",
    skipConfirm: "Isto desliga o portão de publicação para TODOS os escopos deste projeto. Testes obrigatórios deixarão de bloquear e as publicações serão marcadas como puladas, não como testadas. Fica registrado em seu nome.",
    skipConfirmYes: "Sim, pular testes",
    skipHint: "Somente administradores. A alteração é gravada no log de auditoria.",
    skipBannerTitle: "Os testes estão desligados neste projeto",
    skipBannerBody: "Testes obrigatórios não bloqueiam publicações. Os portões informam “pulado”, não “aprovado”.",
    scopeFilter: "Escopo:",
    allScopes: "Todos os escopos ({{count}})",
    loading: "Carregando o conjunto de testes…",
    loadFailed: "Não foi possível carregar o conjunto de testes",
    runFailed: "Não foi possível iniciar a execução",
    empty: "Ainda não há testes. Eles são gerados na implementação do escopo.",
    cycle: "Versão {{version}}",
    cycleStatus: { open: "Em desenvolvimento", uat: "Em UAT", production: "Em produção", superseded: "Substituída" },
    humanPending: "{{count}} teste(s) de usuário aguardando liberação para UAT",
    runGate: "Executar {{gate}}",
    noItemsForGate: "Nenhum teste no portão {{gate}}",
    runRunning: "Execução em andamento",
    runStalled: "Execução travada — sem resposta do executor",
    runTotals: "{{passed}} aprovados · {{failed}} falharam · {{total}} no total",
    required: "Obrigatório",
    origin: { ai: "IA", user: "Testador" },
    inherit: "Herda", override: "Substituir",
    forceInherited: "Herda a obrigatoriedade do escopo",
    forceOverridden: "Substitui a obrigatoriedade do escopo",
    forceScopeTitle: "Teste obrigatório",
    forceScopeLabel: "Exigir aprovação dos {{count}} testes deste escopo",
    forceScopeHint: "Quando ativo, o produto não avança para produção sem a aprovação destes testes.",
    filter: { all: "Todos", outstanding: "Pendentes", forced: "Obrigatórios", failing: "Com falha" },
  },
  userTest: {
    loading: "Carregando seus testes…",
    loadFailed: "Não foi possível carregar seus testes",
    signoffFailed: "Não foi possível registrar",
    dismissFailed: "Não foi possível descartar o reteste",
    notReleasedTitle: "Ainda não está pronto para você",
    notReleasedBody: "{{count}} teste(s) aguardando. Eles são liberados quando os testes automáticos desta versão passam.",
    summary: "{{required}} obrigatórios · {{optional}} opcionais · versão {{version}}",
    required: "Obrigatório", optional: "Opcional",
    testerAuthored: "Adicionado por um testador",
    addTest: "Adicionar um teste",
    preconditions: "Antes de começar", steps: "Passos", postconditions: "Verificar depois",
    action: "Faça isto", expected: "Você deve ver",
    pass: "Aprovado", fail: "Falhou",
    notesPlaceholder: "O que aconteceu? (obrigatório se falhou)",
    failNeedsReason: "Diga o que realmente aconteceu — isso vira o relato de erro.",
    failHint: "Uma falha cria um relato de erro com estes passos anexados.",
    bugFiled: "Relato de erro criado com seus passos e o que você viu.",
    viewBug: "Abrir o relato de erro",
    retestTitle: "Por favor, teste novamente",
    retestImpact: "O código coberto por este teste mudou: {{path}} (a {{hops}} passo(s)).",
    retestBroad: "Uma mudança de grande alcance chegou, então todos os testes de usuário estão sendo reverificados.",
    retestUndetermined: "Não foi possível determinar se a mudança afeta este teste, por isso estamos perguntando.",
    dismissRetest: "Não afetado",
    dismissExplain: "Se a mudança não pôde afetar o que você verificou, explique por quê. Sua aprovação anterior permanece.",
    dismissPlaceholder: "Por que esta mudança não afeta este teste",
    confirmDismiss: "Descartar reteste",
  },
  addTest: {
    title: "Adicionar um teste a partir do que você encontrou",
    intro: "Você encontrou algo que a especificação não previu. Registre para que seja verificado a cada versão.",
    whatHappened: "O que deve ser verdade",
    titlePlaceholder: "ex. Mudar de fase com um timer ativo mantém a duração correta",
    setup: "Preparação (uma por linha)",
    setupPlaceholder: "ex. Um projeto com um timer em execução",
    steps: "Passos",
    actionPlaceholder: "Faça isto…",
    expectedPlaceholder: "Você deve ver… (um valor, uma contagem, uma mensagem)",
    addStep: "Adicionar passo",
    after: "Verificar depois (uma por linha)",
    afterPlaceholder: "ex. O total de horas registradas não muda",
    vagueWarning: "O(s) passo(s) {{steps}} dizem algo como \"funciona\". Diga o que deve aparecer: um valor, uma contagem ou uma mensagem.",
    needTitle: "Dê um título ao teste",
    needStep: "Adicione pelo menos um passo",
    needExpected: "Cada passo precisa de um resultado esperado",
    failed: "Não foi possível salvar o teste",
    cancel: "Cancelar", save: "Salvar teste",
  },
  qaPhase: {
    testTasks: "Tarefas de teste",
    passCount: "{{n}} aprovado",
    failCount: "{{n}} falhou",
  },
  humanTestingPhase: {
    feedbackItems: "Itens de feedback",
    issuesRaisedByTesters: "Problemas levantados por testadores humanos",
    openCount: "{{count}} aberto(s)",
  },
  betaPhase: {
    approvalTitle: "Fase Beta — Aprovação do administrador necessária",
    approvalBody: "A criação de novas tarefas durante a fase Beta requer aprovação explícita do administrador. Isso será registrado na trilha de auditoria.",
    approveAndCreate: "Aprovar e criar",
    feedbackTitle: "Feedback do Beta",
    feedbackHint: "As modificações exigem aprovação do administrador",
    openCount: "{{count}} aberto(s)",
    creatingApprovedTask: "Criando a tarefa aprovada...",
  },
  releasePhase: {
    statusComplete: "Concluído",
    statusInProgress: "Em andamento",
    statusReopened: "Reaberto",
    statusPending: "Pendente",
    manualDeployment: "Implantação manual",
    ciCdPipeline: "Pipeline CI/CD",
    stagedRollout: "Implantação em etapas",
    hotfix: "Correção urgente",
    releaseRecord: "Registro de versão",
    priorPhasesIncomplete: "Fases anteriores incompletas",
    versionNumber: "Número da versão",
    versionPlaceholder: "ex.: v1.0.0",
    releaseDate: "Data de versão",
    releaseMethod: "Método de versão",
    releaseNotes: "Notas de versão",
    notesPlaceholder: "Resuma o que foi lançado, as principais alterações, os problemas conhecidos...",
    saved: "Salvo",
    saveNotes: "Salvar notas",
    fullLifecycleSummary: "Resumo completo do ciclo de vida",
    columnPhase: "Fase",
    columnStatus: "Status",
    columnEntered: "Iniciada",
    columnCompleted: "Concluída",
    projectLabel: "Projeto",
  },
  phaseCompletionBar: {
    advanceFailed: "Falha ao avançar a fase",
    reopenFailed: "Falha ao reabrir a fase",
    checkingCriteria: "Verificando os critérios de conclusão...",
    phaseComplete: "Fase concluída",
    phaseReopened: "Fase reaberta",
    readyToAdvance: "Pronto para avançar",
    criteriaMet: "{{pass}} de {{total}} critérios atendidos",
    collapse: "Recolher",
    expand: "Expandir",
    advancing: "Avançando...",
    markAsReleased: "Marcar como lançado",
    advancePhase: "Avançar fase",
    reopenedForRevisions: "Esta fase foi reaberta para revisões.",
    cancelReopen: "Cancelar reabertura",
    reopenPhase: "Reabrir fase",
    reopenReasonPlaceholder: "Motivo para reabrir esta fase...",
    reopening: "Reabrindo...",
    confirmReopen: "Confirmar reabertura",
  },
  phaseTaskGroup: {
    unassigned: "Não atribuído",
    unnamedScope: "Scope sem nome",
    noTasksInScope: "Nenhuma tarefa neste scope.",
    noTasksInPhase: "Nenhuma tarefa nesta fase.",
    unscopedTasks: "Tarefas sem scope",
    failedToLoad: "Falha ao carregar as tarefas",
    requestFailed: "Falha na solicitação: {{status}}",
  },
  taskList: {
    statusOpen: "Aberto",
    statusInProgress: "Em andamento",
    statusCompleted: "Concluído",
    statusDismissed: "Descartado",
    resultNone: "—",
    resultPass: "Aprovado",
    resultFail: "Reprovado",
    resultBlocked: "Bloqueado",
    priorityLow: "Baixa",
    priorityMed: "Méd.",
    priorityHigh: "Alta",
    priorityCrit: "Crít.",
    taskNamePlaceholder: "Nome da tarefa...",
    descriptionPlaceholder: "Descrição (opcional)...",
    addTaskButton: "Adicionar tarefa",
    addTask: "Adicionar tarefa",
    createError: "Falha ao criar a tarefa",
    noTasks: "Ainda não há tarefas",
    colTask: "Tarefa",
    colResult: "Resultado",
    colRaisedBy: "Relatado por",
    colPriority: "Prioridade",
    colStatus: "Status",
    countOpen: "{{n}} abertas",
    countClosed: "{{n}} fechadas",
    countTotal: "{{n}} no total",
  },
  phase: {
    design: "Concepção",
    implementation: "Implementação",
    qa: "QA",
    humanTesting: "Testes humanos",
    beta: "Beta",
    release: "Lançamento",
  },
  projectPanel: {
    saveFailed: "Falha ao salvar",
    failedToLoad: "Falha ao carregar",
    loadingTasks: "Carregando tarefas…",
    noTasksForScope: "Nenhuma tarefa para este scope.",
    filterAll: "todas",
    filterOpen: "abertas",
    filterClosed: "fechadas",
    noFilterTasks: "Sem tarefas {{filter}}.",
    openTasks: "Tarefas abertas",
    noOpenTasks: "Sem tarefas abertas.",
    statTotal: "Total",
    statOpen: "Aberto",
    statClosed: "Fechado",
    statInProgress: "Em andamento",
    statComplete: "Concluído",
    statActive: "Ativo",
    statResolved: "Resolvido",
    statScopesPassing: "Scopes aprovados",
    statQaTasksDone: "Tarefas QA feitas",
    statNoTasks: "Sem tarefas",
    documents: "Documentos",
    completeCheck: "✓ concluído",
    tasksCount: "Tarefas ({{done}}/{{total}})",
    specOpenClosed: "Spec {{num}}: Abertas {{open}} · Fechadas {{closed}}",
    // [SCOPE 137 / T014] Change Impact agreement log (read-only)
    changeImpactTitle: "Registro de Change Impact",
    changeImpactCount: "Registro de Change Impact ({{count}})",
    changeImpactLoading: "Carregando…",
    changeImpactEmpty: "Nenhum acordo ou cruzamento foi registrado para este scope.",
    changeImpactError: "Não foi possível carregar o registro de Change Impact.",
    changeImpactKindAck: "Reconhecido",
    changeImpactKindApproval: "Aprovado",
    changeImpactKindDeclined: "Recusado",
    changeImpactKindCrossing: "Cruzado sem supervisão",
    changeImpactAffected: "{{count}} afetados",
    changeImpactQueued: "enviado após um período offline",
    changeImpactReadOnly: "Somente leitura. Estes registros não podem ser alterados.",
    reopen: "Reabrir",
    reactivate: "Reativar",
    markClarified: "Marcar esclarecido",
    defer: "Adiar",
    needsClarification: "Requer esclarecimento",
    deleteScope: "Excluir",
    deleteScopeConfirm: "Excluir permanentemente o escopo {{num}} \"{{title}}\" e suas {{tasks}} tarefa(s) e {{docs}} documento(s)? Esta ação não pode ser desfeita.",
    deleteScopeError: "Não foi possível excluir o escopo. Tente novamente.",
    overviewDoc: "Doc de visão geral",
    scopeCountOne: "{{n}} scope",
    scopeCountOther: "{{n}} scopes",
    fullyGenerated: "{{n}} totalmente gerados",
    noScopesCreatedYet: "Nenhum scope criado ainda.",
    scopeDocuments: "Documentos de scope",
    noScopes: "Nenhum scope.",
    progressByScope: "Progresso por scope",
    qaStatusByScope: "Status QA por scope",
    allTestsPassed: "Todos os {{n}} testes aprovados",
    inProgressDone: "{{n}} em andamento · {{done}}/{{total}} feitas",
    completeCount: "{{done}}/{{total}} concluídas",
    noQaTasks: "Sem tarefas QA",
    betaApprovalNotice: "Novas tarefas na Beta exigem aprovação de um ADMIN antes da criação.",
    feedbackByScope: "Feedback por scope",
    allScopesClear: "Todos os scopes livres — pronto para publicação.",
    openItemsRemain: "Restam {{n}} itens abertos nos scopes.",
    phaseChecklist: "Lista de verificação de fases",
    openItemsByScope: "Itens abertos por scope",
    openResolved: "{{open}} abertos · {{done}}/{{total}} resolvidos",
    phaseView: "Vista {{phase}}",
    allProjects: "Todos os projetos",
    open: "Abrir",
    status: {
      draft: "Rascunho",
      inReview: "Em revisão",
      pending: "Pendente",
      clarified: "Esclarecido",
      planned: "Planejado",
      tasksGenerated: "Tarefas geradas",
      implemented: "Implementado",
      qaPassed: "QA aprovada",
      humanTested: "Testado por humanos",
      betaCleared: "Beta aprovada",
      released: "Publicado",
      deferred: "Adiado",
    },
    doctype: {
      tasks: "Tarefas",
      datamodel: "Modelo de dados",
      quickstart: "Início rápido",
      checklist: "Lista de verificação",
      contracts: "Contratos",
      research: "Pesquisa",
      overview: "Visão geral",
      stackStyle: "Stack e estilo",
    },
    issuetype: {
      bug: "Bug",
      feature: "Funcionalidade",
      enhancement: "Melhoria",
      task: "Tarefa",
    },
  },
  helpCatalog: {
    ui: {
      included: "Incluído",
      upgrade: "Upgrade",
      admin: "Admin",
      adminOnly: "Apenas administrador",
      howToLabel: "Como fazer:",
      howToUseIt: "Como usar",
      availableFromPrefix: "Disponível a partir do plano ",
      availableFromSuffix: ".",
      yourPlan: "Seu plano",
      subscriptionPlan: "Plano de assinatura",
    },
    category: {
      coreWorkspace: "Espaço de trabalho principal",
      developmentLifecycle: "Ciclo de vida do desenvolvimento",
      integrations: "Integrações",
      teamCollaboration: "Equipe e colaboração",
      timeBillingConsulting: "Tempo, faturamento e consultoria",
      adminConfiguration: "Administração e configuração",
      securityCompliance: "Segurança e conformidade",
      enterpriseSupport: "Enterprise e suporte",
    },
    plan: {
      SINGLE_USER: {
        label: "Usuário único",
        userLimit: "1 usuário",
      },
      SMALL_BUSINESS: {
        label: "Pequena/Média empresa",
        userLimit: "Até 5 usuários",
      },
      CONSULTANT: {
        label: "Consultor",
        userLimit: "Equipe ilimitada",
      },
      ENTERPRISE: {
        label: "Enterprise",
        userLimit: "25+ usuários",
      },
    },
    feature: {
      dashboard: {
        title: "Painel",
        description: "Sua visão geral dos projetos — o progresso das fases do Lifecycle por projeto, além de links rápidos para os documentos gerados de cada projeto.",
        howTo: "Abra o Painel na barra lateral. Cada cartão de projeto mostra o progresso por fase e os links de documentos; os ADMIN têm um botão Novo para criar um projeto diretamente.",
      },
      projects: {
        title: "Projetos",
        description: "Crie e gerencie projetos. Projetos ilimitados em todos os planos pagos.",
        howTo: "Crie um projeto pelo botão Novo do painel ou pelo menu Projetos e, em seguida, abra-o para ver suas abas (visão geral, lifecycle, tarefas).",
      },
      tasks: {
        title: "Minhas tarefas (Kanban)",
        description: "O trabalho atribuído a você em um quadro Kanban abrangendo todos os projetos.",
        howTo: "Abra Minhas tarefas; arraste os cartões entre as colunas para mudar o status, ou abra um cartão para ver os detalhes.",
      },
      settings: {
        title: "Configurações",
        description: "Configurações de conta, empresa e preferências, incluindo o idioma.",
        howTo: "Abra Configurações no menu; o seletor de idioma e o perfil ficam aqui.",
      },
      faq: {
        title: "Perguntas frequentes",
        description: "Respostas às perguntas comuns sobre o uso do wxKanban.",
        howTo: "Abra as Perguntas frequentes no menu.",
      },
      lifecycle: {
        title: "Lifecycle de 6 fases e orquestrador",
        description: "A metodologia Design → Implementation → QA → HumanTesting → Beta → Release que controla cada funcionalidade e mantém uma única fonte de verdade sobre a situação de um projeto.",
        howTo: "Conduza-o a partir do Claude Code + o kit wxKanban no VS Code (buildscope → createSpecs → implement). O comando certo só está disponível na fase certa.",
      },
      specs: {
        title: "Specs e Scopes",
        description: "Os documentos Scope-of-project e as Specs, planos e tarefas geradas para cada funcionalidade.",
        howTo: "Redija um Scope com buildscope; gere a Spec, o plano e as tarefas com createSpecs; acompanhe-os no Cockpit.",
      },
      audit: {
        title: "Relatórios de auditoria",
        description: "Uma trilha de auditoria por release do que foi construído, testado e delimitado com marcações de código.",
        howTo: "Abra o link Relatório de auditoria em um cartão de projeto ou gere-o novamente no painel de Admin.",
      },
      cockpit: {
        title: "Dev Cockpit",
        description: "Uma barra lateral do VS Code que mostra o trabalho restante por Scope, em tempo real à medida que o trabalho é construído.",
        howTo: "Instale a extensão Dev Cockpit; ela fixa o seu Scope ativo e lista as tarefas não concluídas.",
      },
      pmIntegrations: {
        title: "Integrações PM",
        description: "Conecte Jira, Monday.com, Asana e Trello e sincronize tarefas nos dois sentidos.",
        howTo: "Abra Integrações PM e conecte-se via OAuth; a sincronização é executada em uma programação em segundo plano.",
      },
      team: {
        title: "Gestão de equipe",
        description: "Convide colegas de equipe, atribua funções e gerencie o acesso com controles baseados em funções.",
        howTo: "Abra Equipe; convide por e-mail e defina a função de cada membro.",
      },
      timeTracking: {
        title: "Controle de tempo e faturamento",
        description: "Cronômetro integrado com controle de horas faturáveis e registros de tempo.",
        howTo: "Inicie o cronômetro em uma tarefa; marque os registros como faturáveis para o faturamento.",
      },
      invoices: {
        title: "Faturas",
        description: "Gere faturas em PDF a partir dos registros de tempo faturáveis.",
        howTo: "Abra Faturas, escolha um projeto e um intervalo de datas e gere; os registros faturados são marcados para evitar duplicatas.",
      },
      reports: {
        title: "Relatórios",
        description: "Relatórios avançados sobre tempo, projetos e resultados.",
        howTo: "Abra Relatórios no menu.",
      },
      workHub: {
        title: "Work Hub",
        description: "Um hub de engajamentos de consultoria para gerenciar o trabalho com clientes.",
        howTo: "Abra o Work Hub para ver e gerenciar seus engajamentos.",
      },
      clientPortal: {
        title: "Portal do cliente",
        description: "Ofereça aos clientes uma visão restrita e somente leitura do projeto deles.",
        howTo: "Ative o portal para um projeto e compartilhe o link do cliente.",
      },
      customWorkflows: {
        title: "Fluxos de trabalho personalizados",
        description: "Adapte os fluxos de trabalho do Lifecycle ao processo da sua equipe.",
        howTo: "Configure fluxos de trabalho personalizados nas configurações do projeto.",
      },
      apiAccess: {
        title: "Acesso à API",
        description: "Acesso programático aos seus dados do wxKanban via API.",
        howTo: "Crie um token de API em Configurações e chame os endpoints documentados.",
      },
      stackProfiles: {
        title: "Perfis de Stack",
        description: "Definições reutilizáveis de Stack de destino e de estilo aplicadas quando um projeto é criado.",
        howTo: "Abra os Perfis de Stack (Admin) para definir ou editar os stacks da sua empresa.",
      },
      themeProfiles: {
        title: "Perfis de tema",
        description: "Temas de aparência reutilizáveis para as saídas geradas.",
        howTo: "Abra os Perfis de tema (Admin) para definir ou editar temas.",
      },
      encryption: {
        title: "Criptografia em repouso",
        description: "Os segredos são criptografados em repouso com AES-256-GCM.",
        howTo: "Sempre ativo — nenhuma configuração necessária.",
      },
      soc2: {
        title: "SOC 2 e HIPAA",
        description: "Controles e ferramentas de conformidade alinhados a SOC 2 e HIPAA.",
        howTo: "Adicione os documentos de SDLC da sua empresa para análise de conformidade; atribua controles aos membros da equipe.",
      },
      sso: {
        title: "Login único (SSO)",
        description: "SSO corporativo para a sua organização.",
        howTo: "Entre em contato com seu representante wxKanban para configurar seu provedor de identidade.",
      },
      advancedSecurity: {
        title: "Segurança avançada",
        description: "Controles de segurança adicionais de nível corporativo.",
        howTo: "Configurado como parte da integração Enterprise.",
      },
      emailSupport: {
        title: "Suporte por e-mail",
        description: "Suporte ao produto por e-mail.",
        howTo: "Envie um e-mail para admin@wxperts.com.",
      },
      dedicatedSupport: {
        title: "Suporte dedicado",
        description: "Um contato de suporte dedicado para a sua conta.",
        howTo: "Atribuído durante a integração Enterprise.",
      },
      customIntegrations: {
        title: "Integrações personalizadas",
        description: "Integrações sob medida criadas para a sua organização.",
        howTo: "Defina o escopo das integrações personalizadas com seu representante wxKanban.",
      },
      sla: {
        title: "Garantia de SLA",
        description: "Um SLA contratual de disponibilidade e tempo de resposta.",
        howTo: "Parte do seu contrato Enterprise.",
      },
    },
  },
  // [SCOPE 052] Páginas públicas de Ajuda/Tutoriais (/docs, /docs/:slug)
  docs: {
    title: "Ajuda/Tutoriais",
    backHome: "← Voltar para o início",
    sectionsLabel: "Seções de ajuda",
    navFaq: "Perguntas frequentes",
    navVideos: "Vídeos",
    navSessions: "Sessões de treinamento",
    navGuides: "Guias",
    guidesHeading: "Guias",
    guidesSubtitle: "Guias práticos para baixar e manter junto ao seu projeto.",
    guidesConversionTitle: "Guia de conversão WINDEV / WEBDEV",
    guidesConversionSummary: "Como levar uma aplicação WINDEV ou WEBDEV legada para uma stack moderna de React e PostgreSQL, passo a passo.",
    guidesDraftBadge: "Rascunho",
    guidesDraftNote: "Este guia ainda é um rascunho. Os passos são utilizáveis, mas o texto e os detalhes podem mudar.",
    guidesLoading: "Carregando guias…",
    guidesEmpty: "Ainda não há guias disponíveis.",
    guidesLanguageLabel: "Idioma",
    guidesLanguageAll: "Todos os idiomas",
    faqHeading: "Perguntas frequentes",
    faqSubtitle: "Respostas às perguntas mais comuns sobre o wxKanban.",
    faqEmpty: "Nenhuma pergunta frequente disponível ainda.",
    sessionsHeading: "Próximas sessões de treinamento on-line",
    sessionsSubtitle: "Participe de uma sessão ao vivo com instrutor para começar rapidamente.",
    sessionsEmpty: "Nenhuma sessão está agendada no momento.",
    sessionsRegister: "Registre seu interesse e avisaremos quando a próxima sessão abrir.",
    sessionsCta: "Fale conosco para se inscrever",
    sessionsJoin: "Inscrever-se",
    videosHeading: "Vídeos de treinamento",
    videosSubtitle: "Assista a tutoriais passo a passo do wxKanban.",
    videosEmpty: "Ainda não há vídeos de treinamento disponíveis.",
    loading: "Carregando…",
    loadError: "Não foi possível carregar a documentação. Tente novamente mais tarde.",
    backAll: "← Toda a documentação",
    detailError: "Não foi possível carregar este documento. Tente novamente mais tarde.",
    notFoundTitle: "Não encontrado",
    notFoundBody: "Este documento não existe ou ainda não foi publicado.",
  },
  // [SCOPE 131 / T013] Suspend & cancel copy (FR-011, FR-009).
  subscriptionLifecycle: {
    sectionTitle: "Suspender ou cancelar",
    sectionDesc: "Pause a cobrança por um tempo ou cancele sua assinatura no fim do período que você já pagou.",
    stateActive: "Sua assinatura está ativa.",
    statePaused: "A cobrança está suspensa.",
    statePausedDetail: "Nenhuma fatura está sendo emitida e nenhum dado foi excluído. Retome quando quiser — seus projetos e seu histórico estão esperando.",
    stateCancelPending: "Cancelamento agendado.",
    stateCancelPendingDetail: "Você mantém acesso completo até {{date}}. Pode reverter isso a qualquer momento antes dessa data.",
    effectiveDateUnknown: "o fim do seu período de cobrança atual",
    pauseTitle: "Suspender a cobrança",
    pauseDesc: "A Stripe para de cobrar e sua equipe perde o acesso até você retomar. Nada é excluído e nada fica agendado para exclusão.",
    pauseAction: "Suspender a cobrança",
    unpauseAction: "Retomar a cobrança",
    cancelTitle: "Cancelar a assinatura",
    cancelDesc: "Sua assinatura termina quando acabar o período que você já pagou. Até lá, você mantém acesso completo.",
    cancelAction: "Cancelar a assinatura",
    undoCancelAction: "Manter minha assinatura",
    pauseSuccess: "Cobrança suspensa. Retome quando quiser.",
    unpauseSuccess: "Cobrança retomada — que bom ter você de volta.",
    cancelSuccess: "Sua assinatura terminará em {{date}}. Você pode reverter isso até lá.",
    undoCancelSuccess: "Cancelamento revertido. Sua assinatura continua normalmente.",
    actionFailed: "Não foi possível concluir e nada foi alterado. Tente novamente.",
    dialogTitle: "Cancelar sua assinatura?",
    dialogBody: "Sua assinatura continuará ativa até {{date}}. Depois disso sua equipe perde o acesso e, 30 dias mais tarde, os dados da sua empresa são arquivados.",
    dialogUndoNote: "Você pode reverter isso a qualquer momento antes de {{date}}.",
    dialogTypeLabel: "Digite {{name}} para confirmar",
    dialogPlaceholder: "Nome da empresa",
    dialogConfirm: "Cancelar a assinatura",
    dialogKeep: "Manter minha assinatura",
    dialogMismatch: "Isso não corresponde ao nome da sua empresa.",
    wallPausedTitle: "A cobrança está suspensa",
    wallPausedBody: "Sua empresa suspendeu a própria cobrança, então o produto está pausado. Nada foi excluído. Um administrador pode retomar em Configurações → Cobrança e assinatura.",
    wallPausedCta: "Ir para as configurações de cobrança",
    wallPausedNonAdmin: "Sua empresa suspendeu a cobrança. Nada foi excluído — peça ao administrador para retomar.",
    emailSubject: "Sua assinatura do wxKanban terminará em {{date}}",
    emailHeading: "Seu cancelamento está confirmado",
    emailBody: "Agendamos o fim da sua assinatura do wxKanban para {{date}}. Você mantém acesso completo a {{company}} até essa data e não será cobrado novamente.",
    emailUndoHeading: "Mudou de ideia?",
    emailUndoBody: "Você pode reverter isso a qualquer momento antes de {{date}} em Configurações → Cobrança e assinatura. Nada é excluído enquanto o cancelamento estiver pendente.",
    emailFooter: "Se você não solicitou isso, fale com o administrador da sua empresa imediatamente.",
  },
  // [SCOPE 005 / T019] Time & Billing (SPEC-005). "Resource", never "AI" (constraint 5).
  timeBilling: {
    common: {
      cancel: "Cancelar",
      close: "Fechar",
      delete: "Excluir",
      edit: "Editar",
      loading: "Carregando…",
      save: "Salvar",
      saving: "Salvando…",
    },
    page: {
      title: "Tempo e faturamento",
      open: "Abrir Tempo e faturamento",
      manualOnly: "O tempo de Recursos vindo das execuções de agentes ainda não está disponível. Insira o tempo de Recursos manualmente.",
      engagedManual: "Nos projetos de clientes em que você está alocado, o tempo de Recursos é registrado apenas manualmente.",
      generateStatement: "Gerar extrato",
      billedTotal: "Projetos faturados neste intervalo: {{hours}} h",
      actionFailed: "Não funcionou. Tente novamente.",
      loadFailed: "Não foi possível carregar seus projetos.",
    },
    range: {
      from: "De",
      to: "Até",
      thisWeek: "Esta semana",
      includeInvoiced: "Incluir tempo já faturado",
      invalid: "A data de início deve ser igual ou anterior à data de término.",
    },
    projects: {
      none: "Nenhum projeto ainda.",
      clientProject: "Cliente: {{client}}",
      hours: "{{hours}} h",
      billThis: "Faturável",
      entries: "Lançamentos",
      rates: "Taxas",
      client: "Cliente",
      noClient: "— Sem cliente —",
      billedTo: "Faturado para {{client}}",
      assignClient: "Atribua um cliente para faturar este projeto",
    },
    lines: {
      line: "Linha",
      scope: "Scope",
      developer: "Desenvolvedor",
      resource: "Recurso",
      developerHours: "h Desenvolvedor",
      resourceHours: "h Recurso",
      actions: "Ações",
      addDeveloper: "Adicionar tempo de Desenvolvedor",
      addResource: "Adicionar tempo de Recurso",
      noScopes: "Este projeto ainda não tem scopes.",
      deletedScope: "Scope excluído",
      archived: "arquivado",
      brainstorming: "Brainstorming",
      processes: "Processos",
      dba: "DBA",
      management: "Gestão",
      meetings: "Reuniões",
      projectTotal: "Total do projeto",
    },
    entry: {
      addDeveloper: "Adicionar tempo de Desenvolvedor",
      addResource: "Adicionar tempo de Recurso",
      editTitle: "Editar lançamento de tempo",
      person: "Quem fez o trabalho",
      model: "Modelo",
      date: "Data",
      time: "Tempo",
      timeHelp: "Digite 1:38, 1.5 ou 45m. Um sinal de menos no início indica uma correção.",
      savedAs: "Salvo como {{hours}} h (arredondado para os próximos 15 minutos).",
      notes: "Observações",
      correctionNeedsNote: "Uma correção (tempo negativo) precisa de uma observação.",
      saveFailed: "Não foi possível salvar o lançamento.",
    },
    entries: {
      title: "Lançamentos de tempo — {{project}}",
      none: "Nenhum lançamento neste intervalo.",
      who: "Quem / modelo",
      hours: "h",
      invoiced: "Faturado",
      invoicedHelp: "Este lançamento está em um extrato e não pode mais ser alterado. Adicione uma correção em um período posterior.",
      confirmDelete: "Excluir este lançamento?",
      deleteFailed: "Não foi possível excluir o lançamento.",
      loadFailed: "Não foi possível carregar os lançamentos.",
    },
    timer: {
      title: "Cronômetro",
      idle: "Nenhum cronômetro em andamento. Pressione o relógio de um projeto, ou ▶ em uma linha, para iniciar um.",
      startTitle: "Iniciar cronômetro",
      chooseLine: "Linha",
      startHelp: "Cada início e parada salva um lançamento próprio, então você pode registrar vários intervalos no mesmo dia.",
      runningHere: "O cronômetro está em andamento neste projeto.",
      start: "Iniciar cronômetro",
      stop: "Parar e salvar",
      alreadyRunning: "Já há um cronômetro em andamento.",
      willSave: "Será salvo como {{hours}} h.",
      autoNotes: "Execuções de agentes durante este cronômetro",
      autoUnavailable: "As execuções de agentes ainda não são registradas.",
      noRuns: "Nenhuma execução de agente ainda.",
      refresh: "Atualizar execuções de agentes",
      manualNotes: "Suas observações",
      manualPlaceholder: "ex.: ligação de 15 minutos com o cliente sobre o scope da auditoria",
      notesFailed: "Não foi possível carregar as execuções de agentes.",
      stopFailed: "Não foi possível parar o cronômetro.",
      pause: "Pausar",
      resume: "Retomar",
      paused: "Pausado: este tempo não é cobrado.",
      pauseFailed: "Não foi possível pausar ou retomar o cronômetro.",
    },
    statement: {
      title: "Gerar extrato",
      description: "Crie um extrato para um cliente e um período de faturamento.",
      customer: "Cliente",
      noBilledProjects: "Este cliente ainda não tem projetos faturados",
      cycle: "Ciclo de faturamento",
      cycleWeekly: "Semanal",
      cycleBiweekly: "A cada duas semanas",
      periodStart: "Início do período",
      periodEnd: "Fim do período",
      periodNote: "Somente o tempo faturado e não bloqueado deste período é incluído. Os lançamentos gerados passam a ser somente leitura.",
      generate: "Gerar extrato",
      working: "Processando…",
      created: "Extrato {{name}} criado",
      number: "Número do extrato",
      total: "Total",
      entries: "Lançamentos",
      downloadPdf: "Baixar PDF",
      periodFailed: "Não foi possível carregar o período sugerido",
      cycleFailed: "Não foi possível alterar o ciclo de faturamento",
      generateFailed: "Não foi possível gerar o extrato",
      downloadFailed: "Não foi possível baixar o PDF",
    },
    rates: {
      title: "Taxas do projeto",
      unchanged: "Deixe em branco para manter",
      useDefault: "Usar o padrão da empresa",
      help: "Novos lançamentos usam a taxa em vigor. Os lançamentos existentes mantêm a taxa com que foram criados.",
      companyDefault: "padrão da empresa",
      saved: "Salvo. Desenvolvedor: {{developer}} · Recurso: {{resource}}",
      saveFailed: "Não foi possível salvar as taxas.",
    },
    settings: {
      title: "Tempo e faturamento",
      description: "Taxas por hora padrão e os dados bancários impressos nos extratos dos clientes.",
      developerRate: "Taxa de Desenvolvedor ($/h)",
      resourceRate: "Taxa de Recurso ($/h)",
      ratesHelp: "Um projeto pode substituir qualquer uma das taxas. Alterar uma taxa não modifica os lançamentos existentes.",
      achTitle: "Dados de pagamento ACH",
      achComplete: "Completo",
      achIncomplete: "Incompleto — os extratos informarão que o ACH não está configurado",
      bankName: "Nome do banco",
      accountName: "Nome da conta",
      routingNumber: "Número de roteamento",
      accountNumber: "Número da conta",
      endingIn: "terminando em {{last4}}",
      notSet: "não definido",
      replacePlaceholder: "Digite um novo número para substituí-lo",
      achHelp: "Armazenado criptografado. Aqui só são exibidos os 4 últimos dígitos; os números completos aparecem apenas nos extratos.",
      nothingChanged: "Nada para salvar.",
      saved: "Salvo.",
      saveFailed: "Não foi possível salvar as configurações.",
      loadFailed: "Não foi possível carregar as configurações.",
    },
  },
};
